import {toast} from "sonner";

import {TOAST_CONFIG} from "@/lib/constansts";

import type {HelpTourDefinition} from "../types";

import {getHelpTourById} from "./help-tour-manifest";
import {isHelpTourBlocked} from "./help-tour-guard";
import {
  beginHelpTourSession,
  endHelpTourSession,
  updateHelpTourChrome,
} from "./help-tour-session";
import {
  reconcileHelpTourScrollAfterDriver,
  snapshotSheetScrollForTour,
  scrollHelpTourTargetIntoView,
} from "./help-tour-scroll";
import {queryHelpTarget} from "./help-target";

export type RunHelpTourOptions = {
  showBanner?: boolean;
  restoreScroll?: boolean;
};

type ActiveDriver = {
  destroy: () => void;
};

let activeDriver: ActiveDriver | null = null;

function resolveTourTarget(target: string, fallbackTarget?: string) {
  return (
    queryHelpTarget(target) ??
    (fallbackTarget ? queryHelpTarget(fallbackTarget) : null)
  );
}

function buildDriverSteps(tour: HelpTourDefinition) {
  return tour.steps
    .toSorted((a, b) => a.order - b.order)
    .map((step) => {
      const element = resolveTourTarget(step.target, step.fallbackTarget);
      if (!element) {
        return null;
      }

      const popoverSide = step.side ?? "bottom";

      return {
        element,
        popover: {
          title: step.title,
          description: step.description,
          side: popoverSide,
        },
        popoverSide,
      };
    })
    .filter((step): step is NonNullable<typeof step> => step !== null);
}

export function cancelActiveHelpTour() {
  try {
    activeDriver?.destroy();
  } catch {
    // Driver may already be torn down.
  }
  activeDriver = null;
  endHelpTourSession();
}

export async function runHelpTour(tourId: string, options: RunHelpTourOptions = {}) {
  if (isHelpTourBlocked()) {
    toast.info("Finish required reading before starting a tour.", TOAST_CONFIG);
    return false;
  }

  const tour = getHelpTourById(tourId);
  if (!tour) {
    return false;
  }

  const steps = buildDriverSteps(tour);
  if (steps.length === 0) {
    toast.message("Nothing to highlight on this screen right now.", TOAST_CONFIG);
    return false;
  }

  const [{driver}] = await Promise.all([
    import("driver.js"),
    import("driver.js/dist/driver.css"),
  ]);

  beginHelpTourSession(0, steps.length, options);

  const driverObj = driver({
    animate: true,
    smoothScroll: false,
    showProgress: true,
    progressText: "{{current}} of {{total}}",
    nextBtnText: "Next →",
    prevBtnText: "← Back",
    doneBtnText: "Done",
    allowClose: true,
    overlayOpacity: 0.62,
    stagePadding: 10,
    stageRadius: 10,
    popoverClass: "help-tour-popover",
    steps: steps.map(({element, popover}) => ({element, popover})),
    onHighlightStarted: (element, step, {state}) => {
      const index = typeof state.activeIndex === "number" ? state.activeIndex : 0;
      const popoverSide = steps[index]?.popoverSide ?? "bottom";

      snapshotSheetScrollForTour(element);
      scrollHelpTourTargetIntoView(element, popoverSide);

      // driver.js runs element.scrollIntoView({block:"center"}) synchronously after this hook.
      queueMicrotask(() => {
        reconcileHelpTourScrollAfterDriver(element, popoverSide);
      });
      requestAnimationFrame(() => {
        reconcileHelpTourScrollAfterDriver(element, popoverSide);
        requestAnimationFrame(() => {
          reconcileHelpTourScrollAfterDriver(element, popoverSide);
        });
      });

      updateHelpTourChrome(index, steps.length);
    },
    onHighlighted: (element, _step, {state}) => {
      const index = typeof state.activeIndex === "number" ? state.activeIndex : 0;
      const popoverSide = steps[index]?.popoverSide ?? "bottom";
      reconcileHelpTourScrollAfterDriver(element, popoverSide);
    },
    onCloseClick: (_element, _step, {driver: tourDriver}) => {
      tourDriver.destroy();
      activeDriver = null;
      endHelpTourSession();
    },
    onDestroyed: () => {
      activeDriver = null;
      endHelpTourSession();
    },
  });

  activeDriver = driverObj;

  try {
    driverObj.drive();
  } catch {
    activeDriver = null;
    endHelpTourSession();
    return false;
  }

  return true;
}
